import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { BackArrow, SaveIcon } from "allIcons";
import {
  BodyContainer,
  HeaderContainer,
  IconWrapper,
  InputField,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  SelectField,
  Loader,
  DeleteButton,
  LoadingCard,
  SecondaryHeading,
  UserAvatar,
} from "commonUI";
import { useDefaultRoles, useRoles } from "api/usersModule/roles";
import {
  getUserSavePermission,
  isEqual,
  objectValues,
  parse,
  useSearch,
  useUtilities,
} from "helpers";
import { useCompanies } from "api/usersModule/companies";
import {
  useAddMember,
  useAddUsertoWP,
  useDefaultMembers,
  useDeleteMember,
  useMembers,
  useSiteRelateUsers,
  useUpdateMember,
} from "api/usersModule/members";
import { USERTYPE } from "usersModule/enums";
import { useUserPermissions } from "hooks/user";
import { useUserLimitReached } from "hooks/plan";
import { useSites } from "api/sites";
import { ModulePrefixes, NotificationType, OnBoardingSteps } from "enums";
import "./member.css";
import { useTimeZones, useUploadFiles } from "api/util";
import { t } from "i18next";
import { useDashboardSteps, useUpdateDashboardSteps } from "hooks/dashboard";
export default function AddEditMember({ isSiteModule = false }) {
  const { pathname } = useLocation();
  const { id, "*": path } = useParams();
  const modulePath = path.split("/")[0];
  const { web = null, backurl } = useSearch();
  const { notification, navigate } = useUtilities();
  const { data } = useSites();
  const siteDetails = data?.site_details?.total_sites?.find((site) =>
    isEqual(site.dl_website_id, web)
  );
  const [inputValue, setInputValue] = useState({
    name: "",
    last_name: "",
    email: "",
    user_type: USERTYPE[modulePath] || USERTYPE.TEAM_MEMBER,
    user_role: "",
    wp_user_role: "subscriber",
    user_associated_company: "",
    website_id: "",
    system_status: 0,
    user_avatar: "",
    timezone: null,
  });

  const userTypeTranslation = isEqual(inputValue.user_type, "Team Member")
    ? t("users.module.teamMember")
    : t("users.module.client");

  const [avatarInputFile, setAvatarInputFile] = useState(null);

  const handleInputChange = (key, value) => {
    setInputValue((pre) => ({ ...pre, [key]: value }));
  };
  const { data: roles } = useRoles();
  const { data: defaultRoles } = useDefaultRoles();
  const { data: companies } = useCompanies();
  const {
    data: members,
    isLoading: loadingMembers,
    isStale,
    isRefetching,
  } = useMembers();
  const { data: defaultMembers, isLoading: loadingDefaultMembers } =
    useDefaultMembers();
  const { data: siteUsers, isLoading: loadingSiteUsers } = useSiteRelateUsers();
  const { isLoading, isLimitReached } = useUserLimitReached();
  const { mutateAsync: addMember, isLoading: addingMember } = useAddMember(
    isSiteModule ? `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/users` : "/users/" + modulePath,
    userTypeTranslation
  );
  const { mutateAsync: uploadFiles, isLoading: uploadingFile } =
    useUploadFiles();

  const { mutate: deleteMember, isLoading: deletingMember } = useDeleteMember(
    isSiteModule
      ? `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/users${web ? `?web=${web}` : ""}`
      : "/users/" + modulePath,
    userTypeTranslation
  );
  const { mutateAsync: updateMember, isLoading: updatingMember } =
    useUpdateMember(
      isSiteModule ? t("users.module.user") : userTypeTranslation
    );
  const { mutateAsync: addUsertoWP, isLoading: addingUserToWP } =
    useAddUsertoWP();
  const { data: timeZones, isLoading: loadingTimeZone } = useTimeZones();
  const { data: dashboardSteps } = useDashboardSteps();
  const { mutate: updateDashboardStep } = useUpdateDashboardSteps();

  const userAvatarFileInput = useRef();

  const {
    app_users_add_new_users,
    app_users_delete_users,
    app_users_edit_users,
    add_new_items_to_site_template,
    edit_item_details_in_site_template,
    app_users_view_users,
    isLoading: isPermissionsLoading,
  } = useUserPermissions();

  const userRoles = {};
  (web || isSiteModule ? defaultRoles : roles)
    .filter((_) => _)
    .forEach((role) => {
      if (!isEqual(role.nick, "owner")) {
        userRoles[role.user_role] = {
          ...userRoles[role.user_role],
          [role.role_title]: {
            id: role.id,
            website_id: role.website_id,
          },
        };
      }
    });

  useEffect(() => {
    if (isPermissionsLoading) return;
    if (!app_users_view_users) {
      navigate(isSiteModule ? "/dashboard" : "/users/roles");
      return;
    }
  }, [isPermissionsLoading, app_users_view_users]);

  useEffect(() => {
    if (
      loadingMembers ||
      isEqual(id, "add") ||
      !members ||
      loadingDefaultMembers ||
      !defaultMembers ||
      !siteUsers ||
      loadingSiteUsers ||
      isStale ||
      isRefetching
    )
      return;
    let teamMember = null;
    if (web) {
      teamMember = siteUsers.find((teamMember) => isEqual(teamMember.id, id));
    } else {
      teamMember = [...members, ...defaultMembers].find((teamMember) =>
        isEqual(teamMember.id, id)
      );
    }
    if (!teamMember) return;
    setInputValue({
      ...teamMember,
      user_avatar: teamMember?.user_avatar ?? "",
      user_role: teamMember?.user_role ?? teamMember?.roles?.id ?? "",
    });
  }, [
    members,
    loadingMembers,
    loadingDefaultMembers,
    defaultMembers,
    web,
    siteUsers,
    loadingSiteUsers,
    id,
    isStale,
    isRefetching,
  ]);

  async function handleSubmit() {
    if (inputValue.name === "") {
      notification({
        type: "error",
        message: t("users.teamMember.error.addFirstName"),
      });
      return;
    }
    if (inputValue.last_name === "") {
      notification({
        type: "error",
        message: t("users.teamMember.error.addLasttName"),
      });
      return;
    }
    if (inputValue.user_role === "") {
      notification({
        type: "error",
        message: t("users.teamMember.error.selectUserRole"),
      });
      return;
    }
    if (inputValue.email === "") {
      notification({
        type: "error",
        message: t("users.teamMember.error.addEmail"),
      });
      return;
    }
    if (
      inputValue.email !== "" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue.email)
    ) {
      notification({
        type: "error",
        message: t("users.teamMember.error.addValidEmail"),
      });
      return;
    }
    let avatarUploadURL = null;
    if (avatarInputFile) {
      await uploadFiles({
        product_logo: avatarInputFile,
      }).then((res) => {
        avatarUploadURL = res.data;
      });
    }

    if (isEqual(id, "add")) {
      addMember({
        data: {
          ...inputValue,
          website_id: web ? [web] : null,
          default_tem: isSiteModule && !web ? "1" : "0",
          user_avatar: avatarUploadURL ?? inputValue.user_avatar,
        },
      }).then((res) => {
        if (isSiteModule && web && res.status_code === 200) {
          addUsertoWP({
            web_url: siteDetails.web_url,
            email: res.data.email,
            first_name: res.data.name,
            last_name: res.data.last_name,
            wpsl_user_id: res.data.id,
            wp_user_role: res.data.wp_user_role,
            "HTTP-DL-WEBSITE-KEY": siteDetails.dl_website_id,
          });
        }
        if (
          res.status_code === 200 &&
          dashboardSteps &&
          !dashboardSteps[OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS]
        ) {
          updateDashboardStep({
            name: OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS,
          });
        }
      });
    } else {
      const tempRole = [...defaultRoles, ...roles].find((role) =>
        isEqual(role.id, inputValue.user_role)
      );
      updateMember({
        ...inputValue,
        user_id: id,
        single_site_id: web,
        roles: tempRole ? tempRole : inputValue.roles,
        user_avatar: avatarUploadURL ?? inputValue.user_avatar,
      }).then((res) => {
        if (
          res.status_code === 200 &&
          dashboardSteps &&
          !dashboardSteps[OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS]
        ) {
          updateDashboardStep({
            name: OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS,
          });
        }
      });
    }
    setAvatarInputFile(null);
    if (avatarUploadURL) {
      setInputValue((pre) => ({ ...pre, user_avatar: avatarUploadURL }));
    }
  }

  const handleUploadAvatar = (event) => {
    const inputFile = event.target.files[0];
    if (!inputFile.type.startsWith("image/")) {
      notification({
        type: NotificationType.ERROR,
        message: t("users.teamMember.error.invalidMediaType"),
      });
      return;
    }
    setAvatarInputFile(inputFile);
  };

  const getUserRole = () => {
    if (!inputValue.user_role) return "";

    let userRole = Object.values(userRoles?.[inputValue.user_type] ?? {})?.find(
      (ele) => isEqual(ele.id, inputValue.user_role)
    );

    if (!userRole) return "";
    return isEqual(userRole.website_id, null)
      ? t("users.teamMember.input.wpSitelauncherRole.roleSource.library")
      : t("users.teamMember.input.wpSitelauncherRole.roleSource.siteRole");
  };

  return (
    <>
      <RightMainContainer>
        <Loader
          show={
            addingMember ||
            deletingMember ||
            updatingMember ||
            addingUserToWP ||
            uploadingFile
          }
          loader="block"
        />
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() => {
                navigate(
                  backurl
                    ? backurl
                    : `${pathname.split("/").slice(0, -1).join("/")}${
                        web ? `?web=${web}` : ""
                      }`
                );
              }}
            />
            {loadingDefaultMembers || loadingSiteUsers || loadingMembers ? (
              <LoadingCard className="h-6 w-40" />
            ) : (
              <PrimaryHeading>
                {isEqual(id, "add")
                  ? isSiteModule
                    ? t("users.addUser.title")
                    : isEqual(modulePath, "team-members")
                    ? t("users.addTeamMember.title")
                    : t("users.addClient.title")
                  : inputValue.name + " " + inputValue.last_name}
              </PrimaryHeading>
            )}
          </IconWrapper>
          {(isSiteModule
            ? getUserSavePermission({
                id,
                addPermission: add_new_items_to_site_template,
                editPermission: edit_item_details_in_site_template,
              })
            : getUserSavePermission({
                id,
                addPermission: app_users_add_new_users,
                editPermission: app_users_edit_users,
              })) && (isEqual(id, "add") ? !isLimitReached : true) ? (
            <MainButton
              onClick={handleSubmit}
              disabled={
                isLoading ||
                loadingDefaultMembers ||
                loadingSiteUsers ||
                loadingMembers
              }
            >
              <SaveIcon />
              {t("button.saveChanges")}
            </MainButton>
          ) : (
            ""
          )}
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("users.teamMember.input.firstName.label")}
            placeholder={t("users.teamMember.input.firstName.placeholder")}
            value={inputValue.name}
            setValue={(val) => handleInputChange("name", val)}
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
            inputDivClass="mb-0"
          />
          <InputField
            label={t("users.teamMember.input.lastName.label")}
            placeholder={t("users.teamMember.input.lastName.placeholder")}
            value={inputValue.last_name}
            setValue={(val) => handleInputChange("last_name", val)}
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
            inputDivClass="mb-0"
          />
          <InputField
            label={t("users.teamMember.input.email.label")}
            placeholder={t("users.teamMember.input.email.placeholder")}
            value={inputValue.email}
            setValue={(val) => handleInputChange("email", val)}
            type="email"
            disabled={!isEqual(id, "add")}
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
            inputDivClass="mb-0"
          />
          <div>
            <SecondaryHeading className="text-14">
              {t("users.teamMember.input.userPhoto.label")}
            </SecondaryHeading>
            <div className="mt-1-5 mb-2 flex items-center gap-8">
              {avatarInputFile ||
              (inputValue.user_avatar &&
                inputValue.user_avatar !==
                  "https://api.wpsitelauncher.com/assets/default-avatar.png") ? (
                <img
                  src={
                    avatarInputFile
                      ? URL.createObjectURL(avatarInputFile)
                      : inputValue.user_avatar
                  }
                  className="user_avatar"
                  alt="user avatar"
                />
              ) : (
                <UserAvatar
                  user={inputValue}
                  style={{
                    width: "56px",
                    height: "56px",
                    border: "none",
                  }}
                  textStyle={{
                    fontSize: "16px",
                  }}
                />
              )}
              <MainButton
                onClick={() => {
                  userAvatarFileInput.current.value = null;
                  userAvatarFileInput.current.click();
                }}
              >
                Change
              </MainButton>
              <input
                type="file"
                hidden
                ref={userAvatarFileInput}
                onChange={handleUploadAvatar}
                accept="image/*"
              />
            </div>
          </div>
          <SelectField
            label={t("users.teamMember.input.wordpressRole.label")}
            placeholder={t("users.teamMember.input.wordpressRole.placeholder")}
            value={inputValue.wp_user_role}
            setValue={(val) => {
              setInputValue({
                ...inputValue,
                wp_user_role: val,
              });
            }}
            fieldLabel={inputValue.wp_user_role}
          >
            {["administrator", "editor", "author", "subscriber"].map((role) => (
              <option value={role} className="capitalize">
                {role}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t("users.teamMember.input.wpSitelauncherUserType.label")}
            placeholder={t(
              isEqual(inputValue.is_owner, "1")
                ? "users.companies.usersInCompany.users.owner"
                : "users.teamMember.input.wpSitelauncherUserType.placeholder"
            )}
            value={inputValue.user_type}
            setValue={(val) => {
              setInputValue({
                ...inputValue,
                user_type: val,
                user_role:
                  userRoles[val][Object.keys(userRoles[val])[0]]?.id || "",
              });
            }}
            disabled={isEqual(inputValue.is_owner, "1")}
            fieldLabel={inputValue.role?.user_role}
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
          >
            {objectValues(USERTYPE)
              .filter((role) => !isEqual(role, USERTYPE.OWNER))
              .map((role) => (
                <option value={role}>{role}</option>
              ))}
          </SelectField>
          <SelectField
            label={t("users.teamMember.input.wpSitelauncherRole.label")}
            placeholder={t(
              "users.teamMember.input.wpSitelauncherRole.placeholder"
            )}
            value={inputValue.user_role}
            setValue={(val) => {
              setInputValue({ ...inputValue, user_role: val });
            }}
            disabled={isEqual(inputValue.is_owner, "1")}
            fieldLabel={
              inputValue?.role?.role_title ?? inputValue?.roles?.role_title
            }
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
            secondaryLabel={getUserRole()}
          >
            {Object.keys(userRoles?.[inputValue.user_type] ?? {})?.map(
              (role) => {
                return (
                  <option
                    value={userRoles[inputValue.user_type][role].id}
                    secondaryLabel={
                      isEqual(
                        null,
                        userRoles[inputValue.user_type][role].website_id
                      )
                        ? t(
                            "users.teamMember.input.wpSitelauncherRole.roleSource.library"
                          )
                        : t(
                            "users.teamMember.input.wpSitelauncherRole.roleSource.siteRole"
                          )
                    }
                  >
                    {role}
                  </option>
                );
              }
            )}
          </SelectField>
          <SelectField
            label={t("users.teamMember.input.associatedCompany.label")}
            placeholder={t(
              "users.teamMember.input.associatedCompany.placeholder"
            )}
            value={inputValue.user_associated_company}
            setValue={(val) => {
              setInputValue({ ...inputValue, user_associated_company: val });
            }}
            isLoading={
              loadingDefaultMembers || loadingSiteUsers || loadingMembers
            }
          >
            {companies.map((company) => (
              <option value={company.id}>{company.company_name}</option>
            ))}
          </SelectField>
          <SelectField
            label={t("users.teamMember.input.timezone.label")}
            placeholder={t("users.teamMember.input.timezone.placeholder")}
            value={inputValue.timezone}
            setValue={(val) => {
              setInputValue({ ...inputValue, timezone: val });
            }}
            isLoading={
              loadingDefaultMembers ||
              loadingSiteUsers ||
              loadingMembers ||
              loadingTimeZone
            }
            optionSearchable
          >
            {(timeZones?.data ?? [])?.map((timezone) => (
              <option value={timezone.value}>{timezone.title}</option>
            ))}
          </SelectField>
          {!isEqual(id, "add") &&
          !isSiteModule &&
          app_users_delete_users &&
          !isEqual(inputValue.is_owner, 1) ? (
            <DeleteButton
              type="button"
              deleteMessage={t(
                "users.teamMember.delete.confirmation.description",
                {
                  userType: userTypeTranslation,
                }
              )}
              deleteTitle={t("users.teamMember.delete.confirmation.title", {
                userType: userTypeTranslation,
              })}
              deleteButtonText={t("common.popup.button.confirmDelete")}
              className="mt-3"
              style={{ left: "0" }}
              position="top"
              title={t("users.teamMember.delete.section.title", {
                userType: userTypeTranslation,
              })}
              message={t("users.teamMember.delete.section.description", {
                userType: userTypeTranslation,
              })}
              buttonText={t("users.teamMember.delete.section.button", {
                userType: userTypeTranslation,
              })}
              onDelete={() => {
                if (parse(inputValue.website_id)?.length > 0) {
                  notification({
                    type: "error",
                    message: t(
                      "users.teamMember.delete.error.associatedWithSite"
                    ),
                  });
                } else {
                  deleteMember({ user_id: id, added_by: inputValue.added_by });
                }
              }}
            />
          ) : (
            ""
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
